import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Anchor } from "lucide-react";

const NAV_LINKS = [
  { name: "Home", path: "/" },
  { name: "Menu", path: "/menu" },
  { name: "Book Seaside Bites", path: "/book" },
  { name: "Contact", path: "/contact" },
];

const NavLink = ({ link, isActive, scrolled, isMobile, onClick }) => {
  const baseClasses = "font-body font-semibold transition-colors duration-300 relative";
  
  const getDesktopClasses = () => {
    if (scrolled) {
      return isActive ? "text-seaside-magenta" : "text-seaside-navy hover:text-seaside-magenta";
    }
    return isActive ? "text-seaside-gold" : "text-white hover:text-seaside-gold";
  };

  const getMobileClasses = () => {
    return isActive
      ? "text-seaside-magenta"
      : "text-seaside-navy hover:text-seaside-magenta";
  };

  if (isMobile) {
    return (
      <Link
        to={link.path}
        data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
        onClick={onClick}
        className={`block text-lg py-2 ${baseClasses} ${getMobileClasses()}`}
      >
        {link.name}
      </Link>
    );
  }

  return (
    <Link
      to={link.path}
      data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
      className={`${baseClasses} ${getDesktopClasses()}`}
    >
      {link.name}
      {isActive && (
        <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-current rounded-full" />
      )}
    </Link>
  );
};

const PhoneLink = ({ scrolled, isMobile }) => {
  const classes = isMobile
    ? "flex items-center gap-2 font-body font-bold text-seaside-magenta py-2"
    : `flex items-center gap-2 font-body font-bold transition-colors duration-300 ${
        scrolled ? "text-seaside-magenta" : "text-white"
      }`;

  return (
    <a
      href="tel:727-833-9500"
      data-testid={isMobile ? "mobile-navbar-phone" : "navbar-phone"}
      className={classes}
    >
      <Phone className="w-5 h-5" />
      <span>727-833-9500</span>
    </a>
  );
};

const DesktopNav = ({ scrolled, location }) => (
  <>
    <div className="hidden md:flex items-center gap-8">
      {NAV_LINKS.map((link) => (
        <NavLink
          key={link.path}
          link={link}
          isActive={location.pathname === link.path}
          scrolled={scrolled}
          isMobile={false}
        />
      ))}
    </div>
    <div className="hidden md:flex items-center gap-4">
      <PhoneLink scrolled={scrolled} isMobile={false} />
    </div>
  </>
);

const MobileNav = ({ isOpen, setIsOpen, location }) => {
  if (!isOpen) return null;

  return (
    <div
      data-testid="mobile-menu"
      className="md:hidden glass border-t border-seaside-magenta/20"
    >
      <div className="px-4 py-6 space-y-4">
        {NAV_LINKS.map((link) => (
          <NavLink
            key={link.path}
            link={link}
            isActive={location.pathname === link.path}
            scrolled={true}
            isMobile={true}
            onClick={() => setIsOpen(false)}
          />
        ))}
        <PhoneLink scrolled={true} isMobile={true} />
      </div>
    </div>
  );
};

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const menuButtonClasses = scrolled
    ? "text-seaside-navy hover:bg-seaside-sand"
    : "text-white hover:bg-white/20";

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="navbar-logo"
            className="flex items-center gap-2 group"
          >
            <Anchor
              className={`w-8 h-8 transition-colors duration-300 ${
                scrolled ? "text-seaside-magenta" : "text-white"
              } group-hover:text-seaside-gold`}
            />
            <span
              className={`font-heading font-bold text-xl transition-colors duration-300 ${
                scrolled ? "text-seaside-navy" : "text-white"
              }`}
            >
              Seaside Bites
            </span>
          </Link>

          <DesktopNav scrolled={scrolled} location={location} />

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsOpen(!isOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors duration-300 ${menuButtonClasses}`}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <MobileNav isOpen={isOpen} setIsOpen={setIsOpen} location={location} />
    </nav>
  );
};
